import {
  RainbowMetadataResponse,
  RAINBOW_METADATA_VALIDATION,
} from "../types/IRadarMapRainbow";
import api from "./api";

export default class RadarService {
  /**
   * Fetch Rainbow AI radar metadata from backend API.
   *
   * Makes request to `/v1/radar-map/metadata` endpoint with 30-second timeout.
   * Response contains current snapshot, historical snapshots, forecast times,
   * and CloudFront base URL for constructing tile URLs.
   *
   * @returns {Promise<RainbowMetadataResponse>} Validated metadata response
   * @throws {Error} If request fails, times out, or response validation fails
   *
   * @example
   * const metadata = await radarService.getRainbowMetadata();
   * console.log(metadata.snapshot); // 1701360000
   * console.log(metadata.past_snapshots.length); // 12
   */
  async getRainbowMetadata(): Promise<RainbowMetadataResponse> {
    try {
      const result = await api.get<RainbowMetadataResponse>(
        "/v1/radar-map/metadata",
        {
          timeout: 30000, // 30-second timeout per spec
        }
      );

      // Validate response structure
      this.validateRainbowMetadata(result.data);

      return result.data;
    } catch (error: any) {
      // Re-throw with context
      if (error.code === "ECONNABORTED") {
        throw new Error(
          "Rainbow AI metadata request timed out after 30 seconds"
        );
      }
      if (error.response?.status === 502) {
        throw new Error("Rainbow AI API unavailable (502 Bad Gateway)");
      }
      if (error.response?.status === 504) {
        throw new Error("Rainbow AI API timeout (504 Gateway Timeout)");
      }
      throw new Error(
        `Failed to fetch Rainbow AI metadata: ${error.message || error}`
      );
    }
  }

  /**
   * Validate RainbowMetadataResponse structure per data-model.md rules.
   *
   * Checks:
   * - snapshot is divisible by 600 (10-minute alignment)
   * - past_snapshots contains exactly 12 timestamps
   * - forecast_times contains exactly 13 offsets
   * - cloudfront_base_url starts with https://
   * - oldest past_snapshot within 2-hour retention window
   *
   * @param {RainbowMetadataResponse} metadata - Response to validate
   * @throws {Error} If validation fails
   *
   * @private
   */
  private validateRainbowMetadata(metadata: RainbowMetadataResponse): void {
    const { snapshot, past_snapshots, forecast_times, cloudfront_base_url } =
      metadata;

    // Validate snapshot alignment (divisible by 600)
    if (snapshot % RAINBOW_METADATA_VALIDATION.SNAPSHOT_INTERVAL !== 0) {
      throw new Error(
        `Invalid snapshot: ${snapshot} not aligned to 10-minute interval (must be divisible by 600)`
      );
    }

    // Validate past_snapshots count
    if (
      past_snapshots.length !==
      RAINBOW_METADATA_VALIDATION.PAST_SNAPSHOTS_COUNT
    ) {
      throw new Error(
        `Invalid past_snapshots: expected ${RAINBOW_METADATA_VALIDATION.PAST_SNAPSHOTS_COUNT}, got ${past_snapshots.length}`
      );
    }

    // Validate forecast_times count
    if (
      forecast_times.length !==
      RAINBOW_METADATA_VALIDATION.FORECAST_TIMES_COUNT
    ) {
      throw new Error(
        `Invalid forecast_times: expected ${RAINBOW_METADATA_VALIDATION.FORECAST_TIMES_COUNT}, got ${forecast_times.length}`
      );
    }

    // Validate CloudFront URL format
    if (
      !RAINBOW_METADATA_VALIDATION.CLOUDFRONT_URL_PATTERN.test(
        cloudfront_base_url
      )
    ) {
      throw new Error(
        `Invalid cloudfront_base_url: must start with https:// (got: ${cloudfront_base_url})`
      );
    }

    // Validate retention window (oldest past snapshot within 2 hours)
    const oldestPast = past_snapshots[0];
    const retentionWindow = snapshot - oldestPast;
    if (retentionWindow > RAINBOW_METADATA_VALIDATION.MAX_RETENTION_WINDOW) {
      throw new Error(
        `Invalid retention window: oldest past snapshot ${oldestPast} exceeds 2-hour window (${retentionWindow}s > ${RAINBOW_METADATA_VALIDATION.MAX_RETENTION_WINDOW}s)`
      );
    }
  }
}
